/**
 * ChatHeader Component
 * 
 * Header section of the chat widget including:
 * - Branding and connection status
 * - Action buttons (reset, help, close)
 * - Mode navigation tabs (ChatBot / Recomendador)
 */

import { RotateCcw, HelpCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import chatbotIcon from '/src/assets/historyBlueBotIcon.svg';
import chatBotNavIcon from '/src/assets/chatbot_only_icon.svg';
import recommenderNavIcon from '/src/assets/recommender_only_icon.svg';

export type ChatMode = 'chatbot' | 'recommender';

interface ChatHeaderProps {
  mode: ChatMode;
  isConnected: boolean;
  isResetting: boolean;
  onModeChange: (mode: ChatMode) => void;
  onReset: () => void;
  onClose: () => void;
}

export function ChatHeader({
  mode,
  isConnected,
  isResetting,
  onModeChange,
  onReset,
  onClose,
}: ChatHeaderProps) {
  return (
    <div className="bg-white border-b border-gray-200">
      {/* Accent bar */}
      <div className="h-1 bg-gradient-to-r from-gray-900 via-gray-400 to-gray-200" />
      
      {/* Header content */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <img 
            src={chatbotIcon} 
            alt="CeL.ia"
            className="w-10 object-contain"
          />
          <div>
            <h2 className="font-semibold text-base text-gray-900">
              CeL.ia
            </h2>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className={cn(
                'h-2 w-2 rounded-full',
                isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
              )} />
              <span>{isConnected ? 'En línea' : 'Desconectado'}</span>
            </div>
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={onReset}
            disabled={isResetting}
            className={cn(
              'h-9 w-9 rounded-lg',
              'hover:bg-gray-100 active:bg-gray-200',
              'flex items-center justify-center',
              'transition-colors disabled:opacity-50',
              'text-gray-700'
            )}
            aria-label="Nueva conversación"
          >
            <RotateCcw className={cn('h-4 w-4', isResetting && 'animate-spin')} />
          </button>
          
          <a
            href="https://centrodeelearning.my.site.com/ayuda/s/"
            target="_blank"
            rel="noopener noreferrer"
            className="h-9 w-9 rounded-lg hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center transition-colors text-gray-700"
            aria-label="Ayuda"
          >
            <HelpCircle className="h-4 w-4" />
          </a>
          
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center transition-colors text-gray-700"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Mode Navigation */}
      <div className="px-5 pb-4 pt-2">
        <Tabs value={mode} onValueChange={(value) => onModeChange(value as ChatMode)}>
          <TabsList className="w-full p-1 h-auto gap-1.5">
            <TabsTrigger 
              value="chatbot"
              className='flex items-center gap-2'
            >
              <img 
                src={chatBotNavIcon}
                alt="ChatBot"
                className="object-contain transition-all duration-200 opacity-50 group-data-[state=active]:opacity-100 group-hover:opacity-70 w-5"
              />
              <span className="text-sm font-medium">ChatBot</span>
            </TabsTrigger>
            <TabsTrigger 
              value="recommender" 
              className='flex items-center gap-2'
            >
              <img 
                src={recommenderNavIcon}
                alt="Recomendador"
                className="object-contain transition-all duration-200 group-data-[state=active]:opacity-100 group-hover:opacity-70 w-5"
              />
              <span className="text-sm font-medium">Recomendador</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}

